// ** React Imports
import { Fragment, useState } from "react"
// ** Third Party Components
import AsyncSelect from "react-select/async"
import { useForm, Controller, useFieldArray } from "react-hook-form"
import { ArrowLeft, ArrowRight, X, Plus } from "react-feather"
// ** Utils
import { provideOptionsToSelect, checkFieldValidity } from "@lib/util"
import { getAllFromController } from "@lib/api"
// ** Reactstrap Imports
import {
  Label,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Input,
  CardHeader,
  FormText
} from "reactstrap"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
// ** Store Vars
import { useDispatch, useSelector } from "react-redux"
import { setFormData } from "@store/stores/person"

const AddressInfo = ({ stepper }) => {
  const [selectData, setSelectData] = useState()
  // ** Store Vars
  const store = useSelector((state) => state.persons)

  const defaultValues = {}
  if (store.selectedPerson) {
    defaultValues.addresses = store.selectedPerson.addresses.map((address) => ({
      id: address.id,
      addressType: {
        label: address.type.name,
        value: address.type.id
      },
      street: address.street,
      additional: address.additional,
      country: {
        label: address.city.state.country.name,
        value: address.city.state.country.id
      },
      state: {
        label: address.city.state.nameEN,
        value: address.city.state.id
      },
      city: {
        label: address.city.name,
        value: address.city.id
      },
      postCode: {
        label: address.postCode,
        value: address.postCode
      }
    }))
  }

  // ** Hooks
  const dispatch = useDispatch()
  const {
    control,
    setError,
    handleSubmit,
    clearErrors,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues })
  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses"
  })

  // ** Functions
  const onSubmit = (data) => {
    let error = false
    data.addresses.map((item, index) => {
      for (const key in item) {
        if (key === "additional") continue // the only optional field
        if (!checkFieldValidity(item[key])) {
          setError(`addresses[${index}].${key}`, {
            type: "manual",
            message: `Address ${index + 1} : "${key}" is required`
          })
          error = true
        }
      }
    })
    if (!error) {
      dispatch(
        setFormData({
          ...store.formData,
          addresses: data.addresses.map((address) => ({
            id: address.id,
            entityId: store.selectedPerson?.id,
            street: address.street,
            additional: address.additional,
            countryId: address.country.value,
            stateId: address.state.value,
            cityId: address.city.value,
            postCode: address.postCode.value,
            typeId: address.addressType.value
          }))
        })
      )
      stepper.next()
    }
  }
  // Get States or Cities based on selected country
  const loadOptions = async (index, controllerName, filterField, parentId) => {
    const res = await provideOptionsToSelect({
      name: `${controllerName}?filters[${filterField}]=${parentId}`
    })
    setSelectData({ ...selectData, [`${controllerName.toLowerCase()}-${index}`]: res })
  }
  // Get postCodes based on selected city
  const onFocusPostCodeHandler = async (index, cityId) => {
    const formatedPostCodes = []
    const city = await getAllFromController(`Cities/${cityId}`)
    city.postCodes.split(",").map((postCode) => {
      if (postCode !== "") formatedPostCodes.push({ label: postCode, value: postCode })
    })
    setSelectData({ ...selectData, [`postcodes-${index}`]: formatedPostCodes })
  }

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="content-header">
            <h5 className="mb-0">Address</h5>
            <small>Postal address(es)</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              className="btn-prev"
              onClick={() => stepper.previous()}
            >
              <ArrowLeft size={14} className="align-middle me-sm-25 me-0"></ArrowLeft>
              <span className="align-middle d-sm-inline-block d-none">Previous</span>
            </Button>
            <Button type="submit" color="primary" className="btn-next">
              <span className="align-middle d-sm-inline-block d-none">Next</span>
              <ArrowRight size={14} className="align-middle ms-sm-25 ms-0" />
            </Button>
          </div>
        </div>
        <hr />

        {fields.map((item, index) => {
          return (
            <div key={item.id} className="address">
              <div className="divider divider-primary">
                <div className="divider-text fw-bold">Address {index + 1}</div>
              </div>

              <Row>
                <Col md="6" className="mb-1">
                  <Label className="form-label">Address type</Label>
                  <Controller
                    name={`addresses.${index}.addressType`}
                    id={`addresses.${index}.addressType`}
                    control={control}
                    render={({ field }) => (
                      <AsyncSelect
                        {...field}
                        isClearable={false}
                        isSearchable={false}
                        className="react-select"
                        classNamePrefix="select"
                        cacheOptions
                        defaultOptions
                        loadOptions={() => provideOptionsToSelect({ name: "Addresses/Types" })}
                      />
                    )}
                  />
                </Col>

                <Col className="text-end mt-1">
                  <Button.Ripple
                    outline
                    className="btn-icon rounded-circle"
                    color="danger"
                    onClick={() => remove(index)}
                  >
                    <X size={16} />
                  </Button.Ripple>
                </Col>
              </Row>

              <Row>
                <Col md="6" className="mb-1">
                  <Label className="form-label">Street and number *</Label>
                  <Controller
                    control={control}
                    name={`addresses.${index}.street`}
                    render={({ field }) => (
                      <Input
                        placeholder="Mainroad Street 21"
                        invalid={errors[`street${index}`] && true}
                        {...field}
                      />
                    )}
                  />
                </Col>
                <Col md="6" className="mb-1">
                  <Label className="form-label">Additional information</Label>
                  <Controller
                    control={control}
                    name={`addresses.${index}.additional`}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Postal box, Appartment, floor, etc."
                        required={false}
                      />
                    )}
                  />
                </Col>
              </Row>

              <Card className="mt-2" color="primary" outline>
                <CardHeader>
                  <p>
                    <strong>Select order :</strong>
                    <span className="mx-1 badge bg-light-warning">1. Country</span>
                    <ArrowRight size={16} />
                    <span className="mx-1 badge bg-light-success">2. State</span>
                    <ArrowRight size={16} />
                    <span className="mx-1 badge bg-light-info">3. City</span>
                    <ArrowRight size={16} />
                    <span className="mx-1 badge bg-light-primary">4. Postal code</span>
                  </p>
                </CardHeader>

                <CardBody>
                  <Row>
                    <Col md="6" className="mb-1">
                      <Label className="form-label badge bg-light-warning">1. Country</Label>
                      <Controller
                        control={control}
                        name={`addresses.${index}.country`}
                        render={({ field }) => (
                          <AsyncSelect
                            {...field}
                            isClearable={false}
                            isSearchable={true}
                            className="react-select"
                            classNamePrefix="select"
                            cacheOptions
                            defaultOptions
                            onChange={(prev) => {
                              field.onChange(prev)
                              setValue(`addresses.${index}.state`, null)
                              setValue(`addresses.${index}.city`, null)
                            }}
                            loadOptions={(inputValue) =>
                              provideOptionsToSelect({
                                name: `Countries?search=${inputValue}`,
                                apiEndpoint: "crm"
                              })
                            }
                          />
                        )}
                      />
                    </Col>
                    <Col md="6" className="mb-1">
                      <Label className="form-label badge bg-light-success">2. State</Label>
                      <Controller
                        control={control}
                        name={`addresses.${index}.state`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={selectData && selectData[`states-${index}`]}
                            className="react-select"
                            classNamePrefix="select"
                            isClearable={false}
                            onChange={(prev) => {
                              field.onChange(prev)
                              setValue(`addresses.${index}.city`, null)
                            }}
                            onFocus={() => {
                              const countryId = getValues(`addresses.${index}.country.value`)
                              if (countryId) {
                                return loadOptions(index, "States", "countryId", countryId)
                              }
                            }}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6" className="mb-1">
                      <Label className="form-label badge bg-light-info">3. City</Label>
                      <Controller
                        control={control}
                        name={`addresses.${index}.city`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={selectData && selectData[`cities-${index}`]}
                            className="react-select"
                            classNamePrefix="select"
                            isClearable={false}
                            onFocus={() => {
                              const stateId = getValues(`addresses.${index}.state.value`)
                              if (stateId) {
                                return loadOptions(index, "Cities", "stateId", stateId)
                              }
                            }}
                          />
                        )}
                      />
                    </Col>
                    <Col md="6" className="mb-1">
                      <Label className="form-label badge bg-light-primary">4. Postal code</Label>
                      <Controller
                        control={control}
                        name={`addresses.${index}.postCode`}
                        render={({ field }) => (
                          <CreatableSelect
                            {...field}
                            options={selectData && selectData[`postcodes-${index}`]}
                            className="react-select"
                            classNamePrefix="select"
                            onFocus={() => {
                              const cityId = getValues(`addresses.${index}.city.value`)
                              if (cityId) {
                                onFocusPostCodeHandler(index, cityId)
                              }
                            }}
                          />
                        )}
                      />
                      <FormText className="text-muted">You can add a new postal code</FormText>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>
          )
        })}
        <div className="text-center">
          <Button
            className="btn-icon"
            color="outline-primary"
            onClick={() => {
              clearErrors()
              append({
                street: "",
                additional: "",
                country: "",
                state: "",
                city: "",
                postCode: "",
                addressType: { value: 3, label: "Home" }
              })
            }}
          >
            <Plus size={14} />
            <span className="align-middle ms-25">Add</span>
          </Button>
        </div>
        <hr />
        {errors.addresses && (
          <Card color="danger" outline>
            <CardBody>
              <ul>
                {errors.addresses?.map((address, index) => {
                  for (const key in address) {
                    return (
                      <li key={index} className="text-danger">
                        {address[key].message}
                      </li>
                    )
                  }
                })}
              </ul>
            </CardBody>
          </Card>
        )}
      </Form>
    </Fragment>
  )
}

export default AddressInfo
